import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { parseDefinitionOfDone } from './parseDefinitionOfDone';

const newId = (label, taken) => {
  const base = String(label || 'item')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'item';
  let id = base;
  let suffix = 2;
  while (taken.has(id)) {
    id = `${base}-${suffix}`;
    suffix += 1;
  }
  return id;
};

/**
 * Editor for the organization's definition of done.
 *
 * Teams already keep this in a document, and typing fourteen items into a form is
 * nobody's idea of a good time, so pasting the document is the main way in. Ids
 * are never regenerated for an item that stays, because a scope's ticks point at
 * them.
 */
const DefinitionOfDoneSettings = ({
  items = [],
  standard = '',
  defaults = { definitionOfDone: [], dodStandard: '' },
  canManage = false,
  saving = false,
  onSave,
}) => {
  const [draft, setDraft] = useState(items);
  const [draftStandard, setDraftStandard] = useState(standard);
  const [importOpen, setImportOpen] = useState(false);
  const [pasted, setPasted] = useState('');
  const [includeCandidates, setIncludeCandidates] = useState({});
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(null);

  useEffect(() => { setDraft(items); }, [items]);
  useEffect(() => { setDraftStandard(standard); }, [standard]);

  const parsed = useMemo(
    () => (importOpen ? parseDefinitionOfDone(pasted) : { items: [], candidates: [], standard: '' }),
    [importOpen, pasted]
  );

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(items) || draftStandard.trim() !== (standard || '').trim(),
    [draft, draftStandard, items, standard]
  );

  const groups = draft.reduce((acc, item) => {
    const name = item.group || '';
    const existing = acc.find(entry => entry.name === name);
    if (existing) existing.items.push(item);
    else acc.push({ name, items: [item] });
    return acc;
  }, []);

  const applyImport = () => {
    const chosen = [
      ...parsed.items,
      ...parsed.candidates.filter(candidate => includeCandidates[candidate.id]),
    ];
    // Reinstate the document's order: a candidate belongs with its group, not
    // appended after everything else.
    const order = new Map(parsed.items.concat(parsed.candidates).map((item, index) => [item.id, index]));
    chosen.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));

    setDraft(chosen);
    if (parsed.standard) setDraftStandard(parsed.standard);
    setImportOpen(false);
    setPasted('');
    setIncludeCandidates({});
  };

  const removeItem = (id) => setDraft(current => current.filter(item => item.id !== id));

  const saveEdit = () => {
    setDraft(current => current.map(item => (item.id === editing.id ? { ...editing } : item)));
    setEditing(null);
  };

  const saveNew = () => {
    const taken = new Set(draft.map(item => item.id));
    setDraft(current => [...current, { ...adding, id: newId(adding.label, taken) }]);
    setAdding(null);
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        The checklist every scope carries. It lives here rather than in Jira because it is
        your standard, not your workflow — and when a scope's list is complete, its Jira
        issue gets a comment saying so.
      </Typography>

      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="The standard this serves"
        placeholder="A completed scope can be demonstrated end to end, right now."
        value={draftStandard}
        onChange={(e) => setDraftStandard(e.target.value)}
        disabled={!canManage}
        sx={{ mb: 2 }}
      />

      {canManage && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentPasteIcon />}
            onClick={() => setImportOpen(true)}
          >
            Paste a document
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAdding({ label: '', detail: '', group: groups[groups.length - 1]?.name || '' })}
          >
            Add an item
          </Button>
          {defaults.definitionOfDone?.length > 0 && (
            <Button
              size="small"
              onClick={() => {
                setDraft(defaults.definitionOfDone);
                if (!draftStandard.trim()) setDraftStandard(defaults.dodStandard || '');
              }}
            >
              Use the starter checklist
            </Button>
          )}
        </Stack>
      )}

      {draft.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No checklist yet, so no scope can meet the definition of done and the cycle
          scoreboard has nothing to count.
        </Alert>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {`${draft.length} ${draft.length === 1 ? 'item' : 'items'}`}
            {groups.filter(g => g.name).length > 0 && ` in ${groups.length} groups`}
          </Typography>

          {groups.map(group => (
            <Box key={group.name || 'ungrouped'} sx={{ mt: 1.5 }}>
              {group.name && (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
                >
                  {group.name.toUpperCase()}
                </Typography>
              )}
              {group.items.map(item => (
                <Box
                  key={item.id}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.label}</Typography>
                    {item.detail && (
                      <Typography variant="caption" color="text.secondary">{item.detail}</Typography>
                    )}
                  </Box>
                  {canManage && (
                    <>
                      <Tooltip title="Edit wording, keeping any ticks">
                        <IconButton size="small" onClick={() => setEditing({ ...item })}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove this item">
                        <IconButton size="small" onClick={() => removeItem(item.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}

      {canManage && (
        <Button
          variant="contained"
          onClick={() => onSave({ definitionOfDone: draft, dodStandard: draftStandard.trim() })}
          disabled={saving || !dirty}
        >
          {saving ? <CircularProgress size={24} /> : 'Save definition of done'}
        </Button>
      )}

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Paste your definition of done</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Numbered headings become groups, and each "Title: explanation" line becomes an
            item with its explanation kept as help text.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={10}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder={'1. Functional integrity\n[ ] Vertical Slice Complete: The scope delivers end-to-end functionality.'}
          />

          {pasted.trim() && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip size="small" color="primary" label={`${parsed.items.length} items found`} />
                {parsed.standard && <Chip size="small" variant="outlined" label="Standard found" />}
              </Stack>

              {parsed.candidates.length > 0 && (
                <>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {`${parsed.candidates.length} ${parsed.candidates.length === 1 ? 'line reads' : 'lines read'} like items but had no checkbox. Tick any that belong.`}
                  </Alert>
                  {parsed.candidates.map(candidate => (
                    <FormControlLabel
                      key={candidate.id}
                      sx={{ display: 'block' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(includeCandidates[candidate.id])}
                          onChange={(e) => setIncludeCandidates(current => ({
                            ...current,
                            [candidate.id]: e.target.checked,
                          }))}
                        />
                      }
                      label={<Typography variant="body2">{candidate.label}</Typography>}
                    />
                  ))}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyImport} disabled={parsed.items.length === 0}>
            Replace the checklist
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit checklist item</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="What is ticked"
            value={editing?.label || ''}
            onChange={(e) => setEditing(current => ({ ...current, label: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="What it means"
            value={editing?.detail || ''}
            onChange={(e) => setEditing(current => ({ ...current, detail: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Group"
            value={editing?.group || ''}
            onChange={(e) => setEditing(current => ({ ...current, group: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={!editing?.label?.trim()}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(adding)} onClose={() => setAdding(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a checklist item</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            autoFocus
            label="What is ticked"
            value={adding?.label || ''}
            onChange={(e) => setAdding(current => ({ ...current, label: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="What it means"
            value={adding?.detail || ''}
            onChange={(e) => setAdding(current => ({ ...current, detail: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Group"
            value={adding?.group || ''}
            onChange={(e) => setAdding(current => ({ ...current, group: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveNew} disabled={!adding?.label?.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DefinitionOfDoneSettings;
